import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import BEIcon from '@molecules/BEIcon';
import { usePortals } from '@molecules/BEPortal';
import BEButton, { BELoadingButton } from '@molecules/Button/Button';
import BEText from '@molecules/Text';
import { useCallback, useMemo } from 'react';
import ScreenSize from '../../../constants/screen-sizes';
export interface ModalProps {
  id?: string;
  size?:
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | 'xs'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
    | 'full';
  children?: React.ReactNode;
  closable?: boolean;
  maskClosable?: boolean;
  title?: string | null;
  subtitle?: string;
  okText?: string;
  cancelText?: string;
  showCancel?: boolean;
  showOk?: boolean;
  disableButtons?: boolean;
  width?: number;
  buttonsPosition?: 'center' | 'end' | 'space-around';
  color?: 'neutral' | 'primary' | 'error' | 'warning';
  hideFooter?: boolean;
  hideHeader?: boolean;
  bodyPadding?: boolean;
  onOk?: () => void;
  onCancel?: () => void;
  afterClose?: () => void;
  beforeClose?: () => any;
  buttons?: (props: ModalProps) => JSX.Element;
}

export interface AlertProps {
  title?: string;
  subtitle?: string;
  content?: string;
  okText?: string;
  cancelText?: string;
  showOk?: boolean;
  showCancel?: boolean;
  color?: 'neutral' | 'primary' | 'error' | 'warning';
}

export const ModalButtons = ({
  okText,
  cancelText,
  onOk,
  onCancel,
  showCancel = true,
  showOk = true,
  disableButtons = false,
  color,
}: Pick<
  ModalProps,
  | 'okText'
  | 'cancelText'
  | 'onOk'
  | 'onCancel'
  | 'showOk'
  | 'showCancel'
  | 'disableButtons'
  | 'color'
>) => {
  const actionColor = !color || color === 'neutral' ? 'primary' : 'error';

  return (
    <>
      {showCancel && (
        <BEButton
          minWidth={24}
          color="neutral"
          variant="outline"
          onClick={onCancel}
        >
          {cancelText || 'Cancel'}
        </BEButton>
      )}
      {showOk && (
        <BELoadingButton
          color={actionColor}
          disabled={disableButtons}
          onClick={onOk}
        >
          {okText || 'Ok'}
        </BELoadingButton>
      )}
    </>
  );
};

const CloseButton = ({ onCancel }: ModalProps) => {
  return (
    <BEButton
      minWidth={24}
      color="neutral"
      variant="outline"
      onClick={onCancel}
    >
      Close
    </BEButton>
  );
};

const StyledModalContent = styled(ModalContent)<{ modalwidth?: number }>`
  background: #FFFFFF;
  box-shadow: 0px 4px 64px rgba(119, 130, 149, 0.1);
  border-radius: 8px;
  overflow: hidden;
  margin-top: auto !important;
  margin-bottom: auto !important;
  ${({ modalwidth }) => (modalwidth ? css`width: ${modalwidth}px;` : css``)}
  max-width: calc(100vw - 120px) !important;

  @media screen and (max-width: ${ScreenSize.MOBILE_LARGE}) {
    margin-top: unset !important;
    margin-bottom: unset !important;
    max-height: 100% !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 100% !important;
    align-self: end !important;
  }
`;

const StyledCloseButton = styled.button`
  position: absolute;
  width: 48px;
  height: 48px;
  background-color: #FFFFFF;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  z-index: 999;

  &::selection {
    border: none;
  }
`;

const ModalCloseButton = ({ onCancel }: Pick<ModalProps, 'onCancel'>) => {
  return (
    <StyledCloseButton onClick={onCancel}>
      <BEIcon size="lg" name="close" weight="solid" />
    </StyledCloseButton>
  );
};

const ModalComponent = ({
  children,
  title,
  subtitle,
  width,
  buttons,
  closable = true,
  maskClosable = true,
  disableButtons = false,
  hideFooter = false,
  hideHeader = false,
  afterClose,
  beforeClose,
  bodyPadding = true,
  ...props
}: ModalProps) => {
  const Buttons = (!disableButtons && buttons!) || CloseButton;
  const close = useCallback(async () => {
    if (beforeClose && !(await beforeClose())) {
      return;
    }
    props.onCancel?.();
  }, [beforeClose, props]);

  const titleColor =
    !props.color || props.color === 'neutral' ? 'black' : 'error';
  const bodyProps = bodyPadding ? {} : { padding: 0 };

  return (
    <Modal
      size={props.size}
      isOpen
      onClose={close}
      closeOnOverlayClick={maskClosable}
      motionPreset="slideInBottom"
      scrollBehavior="inside"
      preserveScrollBarGap={true}
    >
      <ModalOverlay />
      <StyledModalContent modalwidth={width}>
        {closable && <ModalCloseButton {...props} />}
        {!hideHeader && (
          <ModalHeader paddingY={4} paddingTop={10}>
            <Box display={'flex'} gap={2} alignItems="center">
              {props.color === 'warning' && (
                <BEText color={titleColor}>
                  <BEIcon name="exclamation-triangle" size="lg" />
                </BEText>
              )}
              <BEText type="h1" weight="600" color={titleColor}>
                {title}
              </BEText>
            </Box>
            {subtitle && (
              <BEText size="xl" weight="500" color={'neutral'} darkness="700">
                {subtitle}
              </BEText>
            )}
          </ModalHeader>
        )}
        <ModalBody {...bodyProps}>{children}</ModalBody>
        {!hideFooter && (
          <ModalFooter paddingY={4} gap={4}>
            <Buttons disableButtons={disableButtons} {...props} />
          </ModalFooter>
        )}
      </StyledModalContent>
    </Modal>
  );
};

export const useAlert = () => {
  const portalManager = usePortals();
  const modalId = 'confirm';

  const portal = useMemo(
    () => ({ close: () => portalManager.close(modalId) }),
    [modalId, portalManager]
  );

  return useCallback(
    async ({
      title = '',
      subtitle = '',
      content = '',
      okText = 'Save',
      cancelText = 'Cancel',
      showOk = true,
      showCancel = true,
      color,
    }: AlertProps) =>
      new Promise<boolean>((resolve) => {
        portalManager.open(
          <ModalComponent
            title={title}
            subtitle={subtitle}
            cancelText={cancelText}
            okText={okText}
            showOk={showOk}
            showCancel={showCancel}
            width={560}
            color={color}
            onCancel={() => {
              portal.close();
              resolve(false);
            }}
            onOk={() => {
              portal.close();
              resolve(true);
            }}
            buttons={() => (
              <Flex mt="32px" gap="8px">
                {showCancel && (
                  <BEButton
                    minWidth={24}
                    color={'neutral'}
                    variant="outline"
                    onClick={() => {
                      portal.close();
                      resolve(false);
                    }}
                  >
                    {cancelText || 'Cancel'}
                  </BEButton>
                )}
                {showOk && (
                  <BEButton
                    color={color}
                    onClick={() => {
                      portal.close();
                      resolve(true);
                    }}
                  >
                    {okText || 'Confirm'}
                  </BEButton>
                )}
              </Flex>
            )}
          >
            <BEText>{content}</BEText>
          </ModalComponent>,
          {
            id: modalId,
          }
        );
      }),
    [portal, portalManager]
  );
};

export default ModalComponent;
